import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { resolveKyrosApiUrl } from "../services/apiConfig";
import "../style/AccountPage.css";

const AUTH_STORAGE_KEY = "kyros_auth_v1";
const GOOGLE_SCRIPT_SELECTOR = 'script[data-kyros-google="1"]';
const GOOGLE_SIGNIN_ASSET = "/images/auth/google/google_signin_dark_rd@4x.png";
const GOOGLE_SIGNUP_ASSET = "/images/auth/google/google_signup_dark_rd@4x.png";
const ACCOUNT_REGISTER_PATH = "/account/register";
const LIBRARY_COVER_MAP = {
  zombie_crushers: "/images/zombie_crushers_carousel_2880x960.webp",
  ghost_hunter: "/images/ghost_hunter_carousel_2880x960.webp",
  warfront: "/images/warfront_background.jpg",
};

let googleIdentityClientId = "";
let googleCredentialCallback = null;

function handleGoogleCredential(response) {
  if (typeof googleCredentialCallback === "function") {
    googleCredentialCallback(response);
  }
}

function loadStoredAuth() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return JSON.parse(window.localStorage.getItem(AUTH_STORAGE_KEY) || "null");
  } catch (_error) {
    return null;
  }
}

export default function AccountPage({ mode = "login" }) {
  const authMode = mode === "register" ? "register" : "login";
  const navigate = useNavigate();

  useEffect(() => {
    if (authMode !== "register") {
      return;
    }

    const storedAuth = loadStoredAuth();
    if (storedAuth && (storedAuth.access_token || storedAuth.refresh_token)) {
      navigate("/account", { replace: true });
    }
  }, [authMode, navigate]);

  useEffect(() => {
    let disposed = false;
    const listeners = [];

    const state = {
      config: null,
      auth: loadStoredAuth(),
      googleIssue: "",
      profilePayload: null,
      authTab: authMode,
      profileOriginal: {
        display_name: "",
        avatar_url: "",
        birthdate: "",
      },
      profileDraft: {
        display_name: "",
        avatar_url: "",
        birthdate: "",
      },
      inlineEdit: {
        displayName: false,
        birthdate: false,
      },
      profileFallbackName: "Kyros Player",
    };

    const el = {
      accountPageRoot: document.getElementById("accountPage"),
      authHint: document.getElementById("authHint"),
      authStatus: document.getElementById("authStatus"),
      googleButtonHost: document.getElementById("googleButtonHost"),
      googleAssetButton: document.getElementById("googleAssetButton"),
      googleAssetImage: document.getElementById("googleAssetImage"),
      tabLogin: document.getElementById("tabLogin"),
      tabRegister: document.getElementById("tabRegister"),
      panelLogin: document.getElementById("panelLogin"),
      panelRegister: document.getElementById("panelRegister"),
      loginForm: document.getElementById("loginForm"),
      registerForm: document.getElementById("registerForm"),
      loginEmail: document.getElementById("loginEmail"),
      loginPassword: document.getElementById("loginPassword"),
      regEmail: document.getElementById("regEmail"),
      regPassword: document.getElementById("regPassword"),
      regPasswordConfirm: document.getElementById("regPasswordConfirm"),
      entitlementsList: document.getElementById("entitlementsList"),
      accountOnlyNodes: Array.from(document.querySelectorAll(".account-only")),
      authOnlyNodes: Array.from(document.querySelectorAll(".auth-only")),
      profileOnlyNodes: Array.from(document.querySelectorAll(".profile-only")),
      btnLogin: document.getElementById("btnLogin"),
      btnRegister: document.getElementById("btnRegister"),
      btnLogoutHero: document.getElementById("btnLogoutHero"),
      btnSaveHero: document.getElementById("btnSaveHero"),
      btnEditDisplayName: document.getElementById("btnEditDisplayName"),
      btnEditBirthdate: document.getElementById("btnEditBirthdate"),
      btnAvatarUpload: document.getElementById("btnAvatarUpload"),
      avatarFileInput: document.getElementById("avatarFileInput"),
      heroDisplayName: document.getElementById("heroDisplayName"),
      heroDisplayNameInput: document.getElementById("heroDisplayNameInput"),
      heroEmail: document.getElementById("heroEmail"),
      heroBirthdateValue: document.getElementById("heroBirthdateValue"),
      heroBirthdateInput: document.getElementById("heroBirthdateInput"),
      heroAvatarImage: document.getElementById("heroAvatarImage"),
      heroAvatarInitial: document.getElementById("heroAvatarInitial"),
    };

    if (!el.authStatus || !el.loginForm || !el.registerForm) {
      return undefined;
    }

    const on = (target, eventName, handler) => {
      if (!target) return;
      target.addEventListener(eventName, handler);
      listeners.push(() => target.removeEventListener(eventName, handler));
    };

    const onEntitlementsWheel = (event) => {
      const rail = el.entitlementsList;
      if (!rail || rail.scrollWidth <= rail.clientWidth + 1) {
        return;
      }

      const verticalDelta = Math.abs(event.deltaY);
      const horizontalDelta = Math.abs(event.deltaX);
      if (verticalDelta <= horizontalDelta) {
        return;
      }

      const atStart = rail.scrollLeft <= 0;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
      const wantsPrevious = event.deltaY < 0;
      if ((atStart && wantsPrevious) || (atEnd && !wantsPrevious)) {
        return;
      }

      event.preventDefault();
      rail.scrollLeft += event.deltaY;
    };

    const setStatus = (node, text, kind) => {
      if (disposed || !node) return;
      const nextText = String(text || "").trim();
      node.textContent = nextText;
      if (!nextText) {
        node.className = "status muted hidden";
        return;
      }
      node.className = `status ${kind || "muted"}`;
    };

    const hasActiveAuth = () =>
      !!(state.auth && (state.auth.access_token || state.auth.refresh_token));

    const updateAuthUi = () => {
      const authenticated = hasActiveAuth();
      (el.accountOnlyNodes || []).forEach((node) => {
        node.classList.toggle("hidden", !authenticated);
      });
      (el.authOnlyNodes || []).forEach((node) => {
        node.classList.toggle("hidden", authenticated);
      });
      (el.profileOnlyNodes || []).forEach((node) => {
        node.classList.toggle("hidden", !authenticated);
      });
      if (el.accountPageRoot) {
        el.accountPageRoot.classList.toggle("account-page--authenticated", authenticated);
      }
      if (el.authHint) {
        el.authHint.classList.toggle("hidden", authenticated);
      }
    };

    const saveAuth = (value) => {
      state.auth = value;
      if (typeof window !== "undefined") {
        if (!value) {
          window.localStorage.removeItem(AUTH_STORAGE_KEY);
        } else {
          window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(value));
        }
        window.dispatchEvent(new CustomEvent("kyros-auth-updated"));
      }
      updateAuthUi();
    };

    const setAuthTab = (tab) => {
      const nextTab = tab === "register" ? "register" : "login";
      const loginActive = nextTab === "login";
      state.authTab = nextTab;
      if (el.tabLogin) {
        el.tabLogin.classList.toggle("active", loginActive);
        el.tabLogin.setAttribute("aria-selected", loginActive ? "true" : "false");
      }
      if (el.tabRegister) {
        el.tabRegister.classList.toggle("active", !loginActive);
        el.tabRegister.setAttribute("aria-selected", loginActive ? "false" : "true");
      }
      if (el.panelLogin) {
        el.panelLogin.classList.toggle("hidden", !loginActive);
      }
      if (el.panelRegister) {
        el.panelRegister.classList.toggle("hidden", loginActive);
      }
      if (el.googleAssetImage) {
        el.googleAssetImage.src = loginActive ? GOOGLE_SIGNIN_ASSET : GOOGLE_SIGNUP_ASSET;
        el.googleAssetImage.alt = loginActive ? "Accedi con Google" : "Registrati con Google";
      }
      if (el.googleAssetButton) {
        el.googleAssetButton.setAttribute(
          "aria-label",
          loginActive ? "Accedi con Google" : "Registrati con Google"
        );
      }
    };

    const setAuthSubmitBusy = (mode, busy) => {
      if (mode === "login" && el.btnLogin) {
        el.btnLogin.disabled = !!busy;
        el.btnLogin.textContent = busy ? "Accesso..." : "Accedi";
      }
      if (mode === "register" && el.btnRegister) {
        el.btnRegister.disabled = !!busy;
        el.btnRegister.textContent = busy ? "Registrazione..." : "Registrati";
      }
    };
    const resolveGoogleButtonWidth = () => {
      if (!el.googleAssetButton || typeof el.googleAssetButton.getBoundingClientRect !== "function") {
        return 320;
      }
      const measuredWidth = Math.round(el.googleAssetButton.getBoundingClientRect().width || 0);
      if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) {
        return 320;
      }
      return Math.max(190, Math.min(360, measuredWidth));
    };
    const setGoogleUiReady = (ready) => {
      const nextReady = !!ready;
      if (el.googleButtonHost) {
        el.googleButtonHost.classList.toggle("is-ready", nextReady);
      }
      if (el.googleAssetButton) {
        el.googleAssetButton.dataset.googleReady = nextReady ? "1" : "0";
        // Keep the custom asset clickable so we can show explicit error messages when Google is unavailable.
        el.googleAssetButton.disabled = false;
      }
    };

    const normalizeEmail = (value) => String(value || "").trim().toLowerCase();
    const toNull = (value) => {
      const next = String(value || "").trim();
      return next || null;
    };
    const extractApiMessage = (data, fallback) =>
      String(
        (data && (data.message || data.detail || data.status || data.error)) ||
          fallback ||
          ""
      ).trim();
    const escapeHtml = (value) =>
      String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const normalizeLibraryKey = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    const resolveLibraryCover = (game) => {
      const codeKey = normalizeLibraryKey(game && game.code);
      const nameKey = normalizeLibraryKey(game && game.display_name);
      return LIBRARY_COVER_MAP[codeKey] || LIBRARY_COVER_MAP[nameKey] || "";
    };
    const dateFormatter = new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const normalizeProfileValue = (value) => String(value || "").trim();
    const normalizeBirthdateValue = (value) => String(value || "").trim().slice(0, 10);
    const isGoogleRegistrationRequired = (error) => {
      const payload = error && typeof error === "object" ? error.payload : null;
      if (!payload || typeof payload !== "object") {
        return false;
      }
      const statusValue = normalizeProfileValue(payload.status).toLowerCase();
      const flowValue = normalizeProfileValue(payload.flow).toLowerCase();
      const providerValue = normalizeProfileValue(payload.provider).toLowerCase();
      return (
        (statusValue === "registration_required" || flowValue === "register") &&
        (!providerValue || providerValue === "google")
      );
    };
    const profileDisplayNameFallback = (account) =>
      normalizeProfileValue(account.display_name || account.name || account.email || "Kyros Player");
    const getAvatarInitial = (value) => normalizeProfileValue(value).charAt(0).toUpperCase() || "K";
    const formatBirthdateLabel = (value) => {
      const normalized = normalizeBirthdateValue(value);
      if (!normalized) {
        return "Non impostata";
      }
      const parsed = new Date(`${normalized}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) {
        return normalized;
      }
      return dateFormatter.format(parsed);
    };
    const isProfileDirty = () =>
      normalizeProfileValue(state.profileDraft.display_name) !==
        normalizeProfileValue(state.profileOriginal.display_name) ||
      normalizeProfileValue(state.profileDraft.avatar_url) !==
        normalizeProfileValue(state.profileOriginal.avatar_url) ||
      normalizeBirthdateValue(state.profileDraft.birthdate) !==
        normalizeBirthdateValue(state.profileOriginal.birthdate);
    const updateSaveButtonVisibility = () => {
      if (!el.btnSaveHero) {
        return;
      }
      const dirty = isProfileDirty();
      el.btnSaveHero.classList.toggle("hidden", !dirty);
      el.btnSaveHero.disabled = false;
    };
    const setInlineEditMode = (inputNode, buttonNode, editable) => {
      if (!inputNode || !buttonNode) {
        return;
      }
      inputNode.hidden = !editable;
      inputNode.disabled = !editable;
      inputNode.classList.toggle("is-editing", editable);
      inputNode.classList.toggle("hidden", !editable);
      inputNode.style.display = editable
        ? (inputNode.type === "date" ? "inline-block" : "block")
        : "none";
      buttonNode.classList.toggle("active", editable);
      buttonNode.setAttribute("aria-pressed", editable ? "true" : "false");
      if (editable) {
        inputNode.focus();
        if (typeof inputNode.select === "function" && inputNode.type !== "date") {
          inputNode.select();
        }
      }
    };
    const setDisplayNameEditing = (editable) => {
      state.inlineEdit.displayName = !!editable;
      setInlineEditMode(
        el.heroDisplayNameInput,
        el.btnEditDisplayName,
        state.inlineEdit.displayName
      );
    };
    const setBirthdateEditing = (editable) => {
      state.inlineEdit.birthdate = !!editable;
      setInlineEditMode(
        el.heroBirthdateInput,
        el.btnEditBirthdate,
        state.inlineEdit.birthdate
      );
    };
    const refreshHeroAvatar = (avatarUrl, displayName) => {
      const normalizedUrl = normalizeProfileValue(avatarUrl);
      const fallbackInitial = getAvatarInitial(displayName);
      if (el.heroAvatarInitial) {
        el.heroAvatarInitial.textContent = fallbackInitial;
        el.heroAvatarInitial.classList.toggle("hidden", !!normalizedUrl);
      }
      if (!el.heroAvatarImage) {
        return;
      }
      if (!normalizedUrl) {
        el.heroAvatarImage.removeAttribute("src");
        el.heroAvatarImage.classList.add("hidden");
        return;
      }
      el.heroAvatarImage.src = normalizedUrl;
      el.heroAvatarImage.classList.remove("hidden");
    };
    const syncProfileUiFromDraft = () => {
      const displayValue =
        normalizeProfileValue(state.profileDraft.display_name) ||
        normalizeProfileValue(state.profileFallbackName) ||
        "Kyros Player";
      if (el.heroDisplayName) {
        el.heroDisplayName.textContent = displayValue;
      }
      if (el.heroDisplayNameInput) {
        el.heroDisplayNameInput.value =
          normalizeProfileValue(state.profileDraft.display_name) ||
          normalizeProfileValue(state.profileFallbackName);
      }
      if (el.heroBirthdateInput) {
        el.heroBirthdateInput.value = state.profileDraft.birthdate;
      }
      if (el.heroBirthdateValue) {
        el.heroBirthdateValue.textContent = formatBirthdateLabel(state.profileDraft.birthdate);
      }
      refreshHeroAvatar(
        state.profileDraft.avatar_url,
        displayValue
      );
      updateSaveButtonVisibility();
    };
    const readFileAsDataUrl = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Impossibile leggere il file selezionato."));
        reader.readAsDataURL(file);
      });
    const loadImageElement = (src) =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Impossibile elaborare l'immagine selezionata."));
        image.src = src;
      });
    const compactAvatarImage = async (file) => {
      const sourceDataUrl = await readFileAsDataUrl(file);
      const image = await loadImageElement(sourceDataUrl);
      const side = Math.min(image.width, image.height);
      const sx = Math.floor((image.width - side) / 2);
      const sy = Math.floor((image.height - side) / 2);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Canvas non disponibile per la compressione dell'immagine.");
      }

      const maxLength = 1900;
      const sizes = [96, 80, 72, 64, 56, 48, 40];
      const qualities = [0.85, 0.74, 0.62, 0.5, 0.42];
      for (let i = 0; i < sizes.length; i += 1) {
        const target = sizes[i];
        canvas.width = target;
        canvas.height = target;
        ctx.clearRect(0, 0, target, target);
        ctx.drawImage(image, sx, sy, side, side, 0, 0, target, target);
        for (let j = 0; j < qualities.length; j += 1) {
          const encoded = canvas.toDataURL("image/jpeg", qualities[j]);
          if (encoded.length <= maxLength) {
            return encoded;
          }
        }
      }

      throw new Error(
        "Immagine troppo pesante: scegli un file più leggero o ritagliato (max circa 1 MB)."
      );
    };

    const buildAccountFromResponse = (data, fallbackEmail) =>
      data.account ||
      data.user ||
      data.profile ||
      (fallbackEmail ? { email: fallbackEmail } : null) ||
      {};

    const applyAuthResponse = (data, fallbackEmail) => {
      const accessToken = (data && (data.access_token || data.accessToken || data.token)) || null;
      const refreshToken =
        (data && (data.refresh_token || data.refreshToken)) || null;
      if (!accessToken && !refreshToken) {
        return false;
      }
      saveAuth({
        access_token: accessToken,
        refresh_token: refreshToken,
        account: buildAccountFromResponse(data || {}, fallbackEmail),
      });
      return true;
    };

    const refreshSession = async () => {
      if (!state.auth || !state.auth.refresh_token) {
        return false;
      }
      try {
        const data = await request(
          "/auth/refresh",
          {
            method: "POST",
            skipAuth: true,
            body: JSON.stringify({ refresh_token: state.auth.refresh_token }),
          },
          false
        );
        saveAuth({
          ...state.auth,
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        return true;
      } catch (_error) {
        saveAuth(null);
        setStatus(el.authStatus, "Sessione scaduta. Effettua nuovamente login.", "error");
        return false;
      }
    };

    async function request(path, options, allowRefresh) {
      const opts = { method: "GET", headers: {}, ...(options || {}) };
      opts.headers = { "Content-Type": "application/json", ...(opts.headers || {}) };

      if (state.auth && state.auth.access_token && !opts.skipAuth) {
        opts.headers.Authorization = `Bearer ${state.auth.access_token}`;
      }

      const res = await fetch(resolveKyrosApiUrl(path), opts);
      if (
        res.status === 401 &&
        allowRefresh !== false &&
        state.auth &&
        state.auth.refresh_token &&
        !opts.skipAuth
      ) {
        const refreshed = await refreshSession();
        if (refreshed) {
          return request(path, options, false);
        }
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = (data && (data.error || data.message)) || "Request failed";
        const error = new Error(message);
        error.status = res.status;
        error.payload = data;
        throw error;
      }
      return data;
    }

    const requestGoogleLogin = async (tokenId) =>
      request(
        "/auth/login",
        {
          method: "POST",
          skipAuth: true,
          body: JSON.stringify({ provider: "google", token_id: tokenId }),
        },
        false
      );

    const requestGoogleRegister = async (tokenId) =>
      request(
        "/auth/register",
        {
          method: "POST",
          skipAuth: true,
          body: JSON.stringify({ provider: "google", token_id: tokenId }),
        },
        false
      );

    const requestPasswordLogin = async (email, password) =>
      request(
        "/auth/login",
        {
          method: "POST",
          skipAuth: true,
          body: JSON.stringify({ provider: "password", email, password }),
        },
        false
      );

    const requestPasswordRegister = async (email, password) => {
      const locale = navigator.language || "it-IT";
      return request(
        "/auth/register",
        {
          method: "POST",
          skipAuth: true,
          body: JSON.stringify({ provider: "password", email, password, locale }),
        },
        false
      );
    };

    const ensureGoogleScript = () =>
      new Promise((resolve) => {
        if (typeof window === "undefined") {
          resolve(false);
          return;
        }
        if (window.google && window.google.accounts && window.google.accounts.id) {
          resolve(true);
          return;
        }

        let done = false;
        const complete = (value) => {
          if (done) return;
          done = true;
          resolve(value);
        };

        let script = document.querySelector(GOOGLE_SCRIPT_SELECTOR);
        if (!script) {
          script = document.createElement("script");
          script.src = "https://accounts.google.com/gsi/client";
          script.async = true;
          script.defer = true;
          script.setAttribute("data-kyros-google", "1");
          document.head.appendChild(script);
        }

        script.addEventListener(
          "load",
          () => complete(!!(window.google && window.google.accounts && window.google.accounts.id)),
          { once: true }
        );
        script.addEventListener("error", () => complete(false), { once: true });
        window.setTimeout(
          () => complete(!!(window.google && window.google.accounts && window.google.accounts.id)),
          2500
        );
      });

    const initGoogle = (clientId, scriptReady) => {
      if (
        !clientId ||
        !scriptReady ||
        !window.google ||
        !window.google.accounts ||
        !window.google.accounts.id
      ) {
        state.googleIssue = !clientId
          ? "missing_client_id"
          : !scriptReady
            ? "google_script_unavailable"
            : "google_sdk_unavailable";
        if (el.googleButtonHost) {
          el.googleButtonHost.innerHTML = "";
        }
        setGoogleUiReady(false);
        return;
      }

      state.googleIssue = "";
      googleCredentialCallback = onGoogleCredential;
      if (googleIdentityClientId !== clientId) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        googleIdentityClientId = clientId;
      }

      if (el.googleButtonHost) {
        el.googleButtonHost.innerHTML = "";
        const buttonWidth = resolveGoogleButtonWidth();
        window.google.accounts.id.renderButton(el.googleButtonHost, {
          theme: "filled_black",
          size: "large",
          shape: "pill",
          text: "signin_with",
          width: buttonWidth,
        });
      }
      setGoogleUiReady(true);
    };

    const triggerGoogleFlow = () => {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        return false;
      }
      try {
        window.google.accounts.id.prompt((notification) => {
          if (!notification) {
            return;
          }
          const notDisplayed =
            typeof notification.isNotDisplayed === "function" &&
            notification.isNotDisplayed();
          const skipped =
            typeof notification.isSkippedMoment === "function" &&
            notification.isSkippedMoment();
          if (!notDisplayed && !skipped) {
            return;
          }
          const notDisplayedReason =
            typeof notification.getNotDisplayedReason === "function"
              ? String(notification.getNotDisplayedReason() || "").trim()
              : "";
          const skippedReason =
            typeof notification.getSkippedReason === "function"
              ? String(notification.getSkippedReason() || "").trim()
              : "";
          const reason = notDisplayedReason || skippedReason;
          setStatus(
            el.authStatus,
            reason
              ? `Google non disponibile in questo browser (${reason}).`
              : "Google non disponibile in questo browser.",
            "error"
          );
        });
        return true;
      } catch (_error) {
        return false;
      }
    };

    const fetchConfig = async () => {
      state.config = await request("/auth/portal/config", { skipAuth: true }, false);
      const scriptReady = await ensureGoogleScript();
      initGoogle(state.config.google_client_id, scriptReady);
    };

    const drawEntitlements = (items) => {
      if (!el.entitlementsList) {
        return;
      }
      el.entitlementsList.innerHTML = "";
      const libraryItems = (items || []).filter(
        (item) => String(item && item.status ? item.status : "").toLowerCase() !== "revoked"
      );

      if (!libraryItems.length) {
        el.entitlementsList.innerHTML =
          '<div class="account-library__empty">' +
          "<strong>La tua libreria e vuota</strong>" +
          "<p>Quando riscatti o acquisti un titolo Kyros, lo troverai qui.</p>" +
          "</div>";
        return;
      }

      libraryItems.forEach((item) => {
        const game = item.game || {};
        const title = game.display_name || game.code || "Gioco Kyros";
        const cover = resolveLibraryCover(game);
        const entitlementType = String(item.entitlement_type || "").toLowerCase();
        const status = String(item.status || "").trim();
        const meta =
          entitlementType === "ownership"
            ? "Nella tua libreria"
            : entitlementType
              ? entitlementType
              : "Disponibile";
        const statusLabel = status ? status : "in libreria";
        const fallbackMark = String(title).trim().charAt(0).toUpperCase() || "K";

        const node = document.createElement("div");
        node.className = "account-library__item";
        node.innerHTML =
          `<div class="account-library__poster ${cover ? "" : "account-library__poster--fallback"}">` +
          (cover
            ? `<img src="${escapeHtml(cover)}" alt="${escapeHtml(title)}" loading="lazy" />`
            : `<span class="account-library__fallback-mark">${escapeHtml(fallbackMark)}</span>`) +
          '<div class="account-library__overlay">' +
          `<span class="account-library__badge">${escapeHtml(statusLabel)}</span>` +
          `<h3>${escapeHtml(title)}</h3>` +
          `<p>${escapeHtml(meta)}</p>` +
          "</div>" +
          "</div>";
        el.entitlementsList.appendChild(node);
      });
    };

    const clearProfile = () => {
      state.profilePayload = null;
      state.profileOriginal = { display_name: "", avatar_url: "", birthdate: "" };
      state.profileDraft = { display_name: "", avatar_url: "", birthdate: "" };
      state.profileFallbackName = "Kyros Player";
      if (el.heroDisplayName) {
        el.heroDisplayName.textContent = "Kyros Player";
      }
      if (el.heroEmail) {
        el.heroEmail.textContent = "Nessuna sessione attiva";
      }
      if (el.heroBirthdateValue) {
        el.heroBirthdateValue.textContent = "Non impostata";
      }
      if (el.heroAvatarInitial) {
        el.heroAvatarInitial.textContent = "K";
      }
      refreshHeroAvatar("", "Kyros Player");
      if (el.avatarFileInput) {
        el.avatarFileInput.value = "";
      }
      setDisplayNameEditing(false);
      setBirthdateEditing(false);
      drawEntitlements([]);
      updateSaveButtonVisibility();
    };

    const syncAuthFromStorage = () => {
      const nextAuth = loadStoredAuth();
      state.auth = nextAuth;
      updateAuthUi();
      if (!nextAuth || !(nextAuth.access_token || nextAuth.refresh_token)) {
        clearProfile();
        setAuthTab("login");
        setStatus(el.authStatus, "", "muted");
      }
    };

    const loadProfile = async () => {
      if (!state.auth || !state.auth.access_token) {
        clearProfile();
        return;
      }

      const data = await request("/auth/profile");
      state.profilePayload = data;

      const account = data.account || {};
      if (state.auth) {
        const currentAccountSnapshot = state.auth.account || {};
        const pickAccountField = (key, fallbackValue = "") =>
          Object.prototype.hasOwnProperty.call(account, key)
            ? account[key]
            : fallbackValue;
        const nextAccountSnapshot = {
          ...currentAccountSnapshot,
          ...account,
          email: pickAccountField("email", currentAccountSnapshot.email || ""),
          display_name: pickAccountField("display_name", currentAccountSnapshot.display_name || ""),
          name: pickAccountField("name", currentAccountSnapshot.name || ""),
          avatar_url: pickAccountField("avatar_url", currentAccountSnapshot.avatar_url || ""),
        };
        const accountSnapshotChanged =
          normalizeProfileValue(currentAccountSnapshot.email) !==
            normalizeProfileValue(nextAccountSnapshot.email) ||
          normalizeProfileValue(currentAccountSnapshot.display_name) !==
            normalizeProfileValue(nextAccountSnapshot.display_name) ||
          normalizeProfileValue(currentAccountSnapshot.name) !==
            normalizeProfileValue(nextAccountSnapshot.name) ||
          normalizeProfileValue(currentAccountSnapshot.avatar_url) !==
            normalizeProfileValue(nextAccountSnapshot.avatar_url);
        if (accountSnapshotChanged) {
          saveAuth({
            ...state.auth,
            account: nextAccountSnapshot,
          });
        }
      }
      const heroEmail = account.email || "n/d";
      if (el.heroEmail) {
        el.heroEmail.textContent = heroEmail;
      }

      state.profileFallbackName = profileDisplayNameFallback(account);
      state.profileOriginal = {
        display_name: normalizeProfileValue(account.display_name || account.name || ""),
        avatar_url: normalizeProfileValue(account.avatar_url || ""),
        birthdate: normalizeBirthdateValue((data.profile && data.profile.birthdate) || ""),
      };
      state.profileDraft = { ...state.profileOriginal };

      syncProfileUiFromDraft();
      setDisplayNameEditing(false);
      setBirthdateEditing(false);
      if (el.avatarFileInput) {
        el.avatarFileInput.value = "";
      }

      drawEntitlements(data.entitlements || []);
    };

    const loadAll = async () => {
      updateAuthUi();
      try {
        await loadProfile();
        setStatus(el.authStatus, "", "muted");
      } catch (error) {
        setStatus(el.authStatus, error.message, "error");
      }
    };

    const onGoogleCredential = async (response) => {
      if (!response || !response.credential) {
        setStatus(el.authStatus, "Token Google non valido.", "error");
        return;
      }
      try {
        const googleAuthRequest =
          state.authTab === "register" ? requestGoogleRegister : requestGoogleLogin;
        const data = await googleAuthRequest(response.credential);
        const authed = applyAuthResponse(data, data && data.account ? data.account.email : null);
        if (!authed) {
          throw new Error("Risposta login Google incompleta.");
        }
        setStatus(
          el.authStatus,
          `Accesso effettuato: ${(data.account && data.account.email) || "account"}`,
          "ok"
        );
        await loadAll();
      } catch (error) {
        if (isGoogleRegistrationRequired(error)) {
          if (state.authTab === "register") {
            setStatus(
              el.authStatus,
              "Registrazione Google non completata. Riprova tra qualche secondo.",
              "error"
            );
            return;
          }
          setStatus(
            el.authStatus,
            "Account Google non registrato. Ti reindirizziamo alla registrazione.",
            "muted"
          );
          navigate(ACCOUNT_REGISTER_PATH);
          return;
        }
        setStatus(el.authStatus, error.message, "error");
      }
    };

    const onPasswordLoginSubmit = async (event) => {
      event.preventDefault();
      const email = normalizeEmail(el.loginEmail.value);
      const password = String(el.loginPassword.value || "");

      if (!email || !password) {
        setStatus(el.authStatus, "Inserisci email e password per accedere.", "error");
        return;
      }
      if (password.length < 8) {
        setStatus(el.authStatus, "La password deve avere almeno 8 caratteri.", "error");
        return;
      }

      setAuthSubmitBusy("login", true);
      try {
        const data = await requestPasswordLogin(email, password);
        const authed = applyAuthResponse(data, email);
        if (!authed) {
          throw new Error("Login riuscito ma token sessione non ricevuto.");
        }
        setStatus(el.authStatus, `Accesso effettuato: ${email}`, "ok");
        el.loginPassword.value = "";
        await loadAll();
      } catch (error) {
        setStatus(el.authStatus, error.message || "Login non riuscito.", "error");
      } finally {
        setAuthSubmitBusy("login", false);
      }
    };

    const onPasswordRegisterSubmit = async (event) => {
      event.preventDefault();
      const email = normalizeEmail(el.regEmail.value);
      const password = String(el.regPassword.value || "");
      const confirmPassword = String(el.regPasswordConfirm.value || "");

      if (!email || !password || !confirmPassword) {
        setStatus(el.authStatus, "Compila tutti i campi per registrarti.", "error");
        return;
      }
      if (password.length < 8) {
        setStatus(el.authStatus, "La password deve avere almeno 8 caratteri.", "error");
        return;
      }
      if (password !== confirmPassword) {
        setStatus(el.authStatus, "Le password non coincidono.", "error");
        return;
      }

      setAuthSubmitBusy("register", true);
      try {
        const data = await requestPasswordRegister(email, password);
        const message = extractApiMessage(
          data,
          "Registrazione inviata. Controlla la tua email per confermare l'account."
        );

        setStatus(el.authStatus, message, "ok");
        el.regPassword.value = "";
        el.regPasswordConfirm.value = "";
        setAuthTab("login");
        if (el.loginEmail) {
          el.loginEmail.value = email;
        }
      } catch (error) {
        setStatus(el.authStatus, error.message || "Registrazione non riuscita.", "error");
      } finally {
        setAuthSubmitBusy("register", false);
      }
    };

    const saveProfile = async () => {
      if (!state.auth || !state.auth.access_token) {
        return;
      }

      const payload = {
        display_name: toNull(state.profileDraft.display_name),
        avatar_url: toNull(state.profileDraft.avatar_url),
        birthdate: toNull(state.profileDraft.birthdate),
      };

      try {
        setDisplayNameEditing(false);
        setBirthdateEditing(false);
        if (el.btnSaveHero) {
          el.btnSaveHero.disabled = true;
          el.btnSaveHero.textContent = "Salvataggio...";
        }
        await request("/auth/profile", {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        await loadProfile();
      } catch (_error) {
        // Profile save errors are intentionally silent in UI.
      } finally {
        if (el.btnSaveHero) {
          el.btnSaveHero.disabled = false;
          el.btnSaveHero.textContent = "Salva";
        }
      }
    };

    const onDisplayNameInput = () => {
      if (!el.heroDisplayNameInput) {
        return;
      }
      state.profileDraft.display_name = String(el.heroDisplayNameInput.value || "").trim();
      syncProfileUiFromDraft();
    };

    const onBirthdateInput = () => {
      if (!el.heroBirthdateInput) {
        return;
      }
      state.profileDraft.birthdate = normalizeBirthdateValue(el.heroBirthdateInput.value);
      syncProfileUiFromDraft();
    };

    const onAvatarFileSelected = async (event) => {
      const file = event && event.target && event.target.files ? event.target.files[0] : null;
      if (!file) {
        return;
      }
      if (!String(file.type || "").toLowerCase().startsWith("image/")) {
        return;
      }
      if (file.size > 1024 * 1024) {
        return;
      }
      try {
        const compactDataUrl = await compactAvatarImage(file);
        state.profileDraft.avatar_url = compactDataUrl;
        syncProfileUiFromDraft();
      } catch (_error) {
        // Avatar processing errors are intentionally silent in UI.
      } finally {
        if (el.avatarFileInput) {
          el.avatarFileInput.value = "";
        }
      }
    };

    const logoutCurrent = async () => {
      if (!state.auth || !state.auth.refresh_token) {
        saveAuth(null);
        clearProfile();
        setAuthTab("login");
        navigate("/account", { replace: true });
        return;
      }

      try {
        await request(
          "/auth/logout",
          {
            method: "POST",
            skipAuth: true,
            body: JSON.stringify({ refresh_token: state.auth.refresh_token }),
          },
          false
        );
      } catch (_error) {
        // Ignore logout API errors and clear local session anyway.
      }

      saveAuth(null);
      clearProfile();
      setAuthTab("login");
      navigate("/account", { replace: true });
    };

    on(el.tabLogin, "click", () => setAuthTab("login"));
    on(el.tabRegister, "click", () => setAuthTab("register"));
    on(el.googleAssetButton, "click", () => {
      const opened = triggerGoogleFlow();
      if (!opened) {
        const reasonText =
          state.googleIssue === "missing_client_id"
            ? "Accesso Google non configurato (google_client_id mancante da /auth/portal/config)."
            : "Accesso con Google non disponibile in questo momento.";
        setStatus(
          el.authStatus,
          reasonText,
          "error"
        );
      }
    });
    on(el.loginForm, "submit", onPasswordLoginSubmit);
    on(el.registerForm, "submit", onPasswordRegisterSubmit);
    on(el.entitlementsList, "wheel", onEntitlementsWheel);
    on(el.btnSaveHero, "click", saveProfile);
    on(el.btnLogoutHero, "click", logoutCurrent);
    on(el.btnEditDisplayName, "click", () => {
      setDisplayNameEditing(!state.inlineEdit.displayName);
    });
    on(el.btnEditBirthdate, "click", () => {
      setBirthdateEditing(!state.inlineEdit.birthdate);
    });
    on(el.heroDisplayNameInput, "input", onDisplayNameInput);
    on(el.heroBirthdateInput, "change", onBirthdateInput);
    on(el.btnAvatarUpload, "click", () => {
      if (el.avatarFileInput) {
        el.avatarFileInput.click();
      }
    });
    on(el.avatarFileInput, "change", onAvatarFileSelected);
    on(el.heroAvatarImage, "error", () => {
      if (el.heroAvatarImage) {
        el.heroAvatarImage.classList.add("hidden");
      }
      if (el.heroAvatarInitial) {
        el.heroAvatarInitial.classList.remove("hidden");
      }
    });
    if (typeof window !== "undefined") {
      on(window, "kyros-auth-updated", syncAuthFromStorage);
      on(window, "storage", syncAuthFromStorage);
    }

    (async function bootstrap() {
      try {
        setDisplayNameEditing(false);
        setBirthdateEditing(false);
        setAuthTab(authMode);
        updateAuthUi();
        setGoogleUiReady(false);
        await fetchConfig();
        if (state.auth && state.auth.refresh_token && !state.auth.access_token) {
          await refreshSession();
        }
        await loadAll();
      } catch (error) {
        setStatus(el.authStatus, error.message, "error");
      }
    })();

    return () => {
      disposed = true;
      if (googleCredentialCallback === onGoogleCredential) {
        googleCredentialCallback = null;
      }
      listeners.forEach((removeListener) => removeListener());
    };
  }, [authMode, navigate]);

  const registerView = authMode === "register";
  const pagePath = registerView ? "/account/register" : "/account";
  const pageUrl = `https://kyrosgames.com${pagePath}`;
  const pageTitle = registerView
    ? "Registrazione Kyros Games | Crea il tuo Account"
    : "Account Kyros Games | Accesso e Gestione Profilo";
  const pageDescription = registerView
    ? "Crea il tuo account Kyros Games con email, password o Google e inizia subito."
    : "Accedi al tuo account Kyros Games e gestisci profilo e libreria giochi.";

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={pageUrl}
        robots="noindex, nofollow"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${pageUrl}#webpage`,
              url: pageUrl,
              name: registerView ? "Registrazione Kyros Games" : "Account Kyros Games",
              inLanguage: "it-IT",
              isPartOf: { "@id": "https://kyrosgames.com/#website" },
              breadcrumb: { "@id": `${pageUrl}#breadcrumb` },
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${pageUrl}#breadcrumb`,
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://kyrosgames.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Account",
                  item: "https://kyrosgames.com/account",
                },
                ...(registerView
                  ? [
                      {
                        "@type": "ListItem",
                        position: 3,
                        name: "Registrazione",
                        item: "https://kyrosgames.com/account/register",
                      },
                    ]
                  : []),
              ],
            },
          ],
        }}
      />

      <section className="account-page" id="accountPage">
        <div className="account-page__inner">
          <header className="account-page__hero account-page__hero--auth auth-only">
            <span className="account-page__eyebrow">Entra nel mondo di Kyros Games</span>
            <h1>{registerView ? "Crea il tuo account" : "Accedi a Kyros Games"}</h1>
            <p>
              {registerView
                ? "Un solo account per sito e launcher. Registrati in pochi secondi e conferma l'email per attivare tutte le funzionalità."
                : "Un solo account per sito e launcher. Accedi per gestire profilo e libreria in un'unica dashboard."}
            </p>
          </header>

          <header className="account-page__hero account-page__hero--profile profile-only hidden">
            <div className="account-profile-hero">
              <div className="account-profile-hero__avatar-wrap">
                <div className="account-profile-hero__avatar" aria-hidden="true">
                  <img id="heroAvatarImage" className="hidden" alt="Avatar profilo" loading="lazy" />
                  <span id="heroAvatarInitial">K</span>
                </div>
                <button
                  id="btnAvatarUpload"
                  type="button"
                  className="account-edit-btn account-edit-btn--avatar"
                  aria-label="Carica foto profilo"
                >
                  <span className="account-edit-btn__icon" aria-hidden="true">&#9998;</span>
                </button>
                <input id="avatarFileInput" type="file" accept="image/*" className="hidden" />
              </div>
              <div className="account-profile-hero__main">
                <span className="account-profile-hero__eyebrow">Kyros Account Dashboard</span>
                <h2 className="account-profile-hero__headline">
                  Bentornato, <span id="heroDisplayName">Kyros Player</span>
                  <button
                    id="btnEditDisplayName"
                    type="button"
                    className="account-edit-btn"
                    aria-label="Modifica display name"
                    aria-pressed="false"
                  >
                    <span className="account-edit-btn__icon" aria-hidden="true">&#9998;</span>
                  </button>
                </h2>
                <input
                  id="heroDisplayNameInput"
                  className="account-profile-hero__inline-input hidden"
                  type="text"
                  maxLength="255"
                  placeholder="Display name"
                  hidden
                  disabled
                />
                <p className="account-profile-hero__email" id="heroEmail">
                  Nessuna sessione attiva
                </p>
                <div className="account-profile-hero__field">
                  <span className="account-profile-hero__field-label">Data di nascita</span>
                  <div className="account-profile-hero__field-row">
                    <span className="account-profile-hero__field-value" id="heroBirthdateValue">
                      Non impostata
                    </span>
                    <input id="heroBirthdateInput" type="date" className="hidden" hidden disabled />
                    <button
                      id="btnEditBirthdate"
                      type="button"
                      className="account-edit-btn"
                      aria-label="Modifica data di nascita"
                      aria-pressed="false"
                    >
                      <span className="account-edit-btn__icon" aria-hidden="true">&#9998;</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="account-profile-hero__actions">
                <button id="btnSaveHero" type="button" className="btn-primary hidden">
                  Salva
                </button>
                <button id="btnLogoutHero" type="button" className="btn-danger">
                  Esci
                </button>
              </div>
            </div>
          </header>

          <div id="authStatus" className="status muted hidden"></div>

          <section className="account-page__grid">
            <article className="account-card account-card--auth auth-only" id="authCard">
              <div className="account-page__brand-line">
                <span className="account-page__brand-dot" aria-hidden="true" />
                <strong>Kyros Games Account</strong>
              </div>
              <h2>{registerView ? "Registrati" : "Accedi"}</h2>
              <p className="account-page__auth-switch">
                {registerView ? (
                  <>
                    Hai già un account Kyros Games? <Link to="/account">Accedi</Link>
                  </>
                ) : (
                  <>
                    Non hai un account Kyros Games? <Link to="/account/register">Iscriviti</Link>
                  </>
                )}
              </p>

              <div
                className="account-page__auth-tabs account-page__auth-tabs--ghost"
                role="tablist"
                aria-label="Flussi autenticazione"
              >
                <button
                  id="tabLogin"
                  type="button"
                  className={`account-page__auth-tab ${registerView ? "" : "active"}`}
                  role="tab"
                  aria-selected={registerView ? "false" : "true"}
                  aria-controls="panelLogin"
                >
                  Accedi
                </button>
                <button
                  id="tabRegister"
                  type="button"
                  className={`account-page__auth-tab ${registerView ? "active" : ""}`}
                  role="tab"
                  aria-selected={registerView ? "true" : "false"}
                  aria-controls="panelRegister"
                >
                  Registrati
                </button>
              </div>

              <div
                id="panelLogin"
                className={`account-page__auth-panel ${registerView ? "hidden" : ""}`}
                role="tabpanel"
                aria-labelledby="tabLogin"
              >
                <form id="loginForm" className="account-page__auth-form" noValidate>
                  <div className="account-page__field-grid account-page__field-grid--single">
                    <div className="account-page__field">
                      <label htmlFor="loginEmail">Email</label>
                      <input
                        id="loginEmail"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        placeholder="nome@dominio.com"
                      />
                    </div>
                    <div className="account-page__field">
                      <label htmlFor="loginPassword">Password</label>
                      <input
                        id="loginPassword"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        minLength="8"
                        placeholder="La tua password"
                      />
                    </div>
                  </div>
                  <div className="account-page__aux-row">
                    <Link className="account-page__forgot-link" to="/account/forgot-password">
                      Hai dimenticato la password?
                    </Link>
                  </div>
                  <div className="actions actions--stack">
                    <button id="btnLogin" type="submit" className="btn-primary">
                      Accedi
                    </button>
                  </div>
                </form>
              </div>

              <div
                id="panelRegister"
                className={`account-page__auth-panel ${registerView ? "" : "hidden"}`}
                role="tabpanel"
                aria-labelledby="tabRegister"
              >
                <form id="registerForm" className="account-page__auth-form" noValidate>
                  <div className="account-page__field-grid account-page__field-grid--single">
                    <div className="account-page__field">
                      <label htmlFor="regEmail">Email</label>
                      <input
                        id="regEmail"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        placeholder="nome@dominio.com"
                      />
                    </div>
                    <div className="account-page__field">
                      <label htmlFor="regPassword">Password</label>
                      <input
                        id="regPassword"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength="8"
                        placeholder="Minimo 8 caratteri"
                      />
                    </div>
                    <div className="account-page__field">
                      <label htmlFor="regPasswordConfirm">Conferma Password</label>
                      <input
                        id="regPasswordConfirm"
                        name="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        minLength="8"
                        placeholder="Ripeti la password"
                      />
                    </div>
                  </div>
                  <p className="account-page__auth-inline-note">
                    Dopo la registrazione riceverai una mail per confermare l&apos;indirizzo email.
                  </p>
                  <div className="actions actions--stack">
                    <button id="btnRegister" type="submit" className="btn-primary">
                      Registrati
                    </button>
                  </div>
                </form>
              </div>

              <div className="account-page__auth-divider">
                <span>oppure continua con</span>
              </div>
              <div className="account-page__google-stack">
                <button
                  id="googleAssetButton"
                  type="button"
                  className="account-page__google-asset"
                  data-google-ready="0"
                >
                  <img
                    id="googleAssetImage"
                    src={registerView ? GOOGLE_SIGNUP_ASSET : GOOGLE_SIGNIN_ASSET}
                    alt={registerView ? "Registrati con Google" : "Accedi con Google"}
                    width="350"
                    height="80"
                    loading="lazy"
                  />
                </button>
                <div id="googleButtonHost" className="account-page__google-host" aria-hidden="true" />
              </div>
              <div id="authHint" className="status muted">
                Accedi per sbloccare il tuo profilo e la tua libreria giochi.
              </div>
            </article>

            <article className="account-card account-card--entitlements account-only profile-only hidden">
              <h2>I tuoi giochi</h2>
              <p>I titoli che possiedi nel tuo account Kyros.</p>
              <div id="entitlementsList" className="account-page__entitlements" />
            </article>
          </section>
        </div>
      </section>
    </>
  );
}

